import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, FolderOpen, BookOpen, FileText, Upload, Trash2, Edit, Settings, Users, ExternalLink, Eye, ClipboardList, Sparkles, Loader2, Wand2, UserCheck } from "lucide-react";
import { Session } from "@supabase/supabase-js";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ProjectCard from "@/components/ProjectCard";
import UploadMaterialDialog from "@/components/UploadMaterialDialog";
import ManageMembersDialog from "@/components/ManageMembersDialog";
import WorkspaceLearnerHub from "@/components/learner-hub/WorkspaceLearnerHub";
import TaxonomyGeneratorTab from "@/components/learner-hub/TaxonomyGeneratorTab";
import IndividualAssessmentTab from "@/components/learner-hub/IndividualAssessmentTab";
import DemoRail from "@/components/DemoRail";
import CourseCreationChat from "@/components/CourseCreationChat";
import { useCourseContext } from "@/contexts/CourseContext";
import type { LearnerProfile, Taxonomy, GeneratedTaxonomy, IdentifiedGap } from "@/lib/learnerHub/types";
import { CCM_TAXONOMY, unitById } from "@/lib/learnerHub/constants";

interface Workspace {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  created_at: string;
}

interface Course {
  id: string;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
}

interface Material {
  id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  text_content: string | null;
  summary: string | null;
  created_at: string;
}

const Workspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCourseContext } = useCourseContext();
  
  const [session, setSession] = useState<Session | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadMaterial, setShowUploadMaterial] = useState(false);
  const [showEditWorkspace, setShowEditWorkspace] = useState(false);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [editedWorkspace, setEditedWorkspace] = useState({ name: "", description: "" });
  const [activeTab, setActiveTab] = useState("courses");
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(["courses"]));
  const [taxonomyLibrary, setTaxonomyLibrary] = useState<Taxonomy[]>(() => {
    try {
      const raw = localStorage.getItem(`taxonomy_library_${id}`);
      if (raw) return JSON.parse(raw) as Taxonomy[];
    } catch {}
    return [CCM_TAXONOMY];
  });
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<Set<string>>(new Set());
  const [loadingMaterialFiles, setLoadingMaterialFiles] = useState(false);
  const [materialsChatOpen, setMaterialsChatOpen] = useState(false);
  const [materialsChatFiles, setMaterialsChatFiles] = useState<File[]>([]);
  const [materialsChatTopic, setMaterialsChatTopic] = useState<string | undefined>(undefined);
  const [assessPendingProfile, setAssessPendingProfile] = useState<LearnerProfile | null>(null);

  // ─── Demo rail state ───────────────────────────────────────────────────────
  const [demoMode, setDemoMode] = useState(false);
  const [demoStep, setDemoStep] = useState(1);
  const [demoLearnerCount, setDemoLearnerCount] = useState(0);
  const [demoIdentifiedGaps, setDemoIdentifiedGaps] = useState<IdentifiedGap[]>([]);
  const [demoRequestedSubTab, setDemoRequestedSubTab] = useState<"import" | "profiles" | "competency" | "gap" | "reconcile" | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (session && id) {
      fetchWorkspace();
      fetchCourses();
      fetchMaterials();
    }
  }, [session, id]);

  // Set context so Course Assistant knows we're on the course creation (workspace) page
  useEffect(() => {
    if (workspace && id) {
      setCourseContext({
        courseId: null,
        courseName: null,
        nodeId: null,
        nodeName: null,
        pageType: "workspace",
        workspaceId: id,
        workspaceName: workspace.name ?? null,
      });
    }
    // setCourseContext is stable (useCallback); omit from deps to avoid re-run when context value ref changes after update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, id]);

  const handleTaxonomySaved = (taxonomy: GeneratedTaxonomy) => {
    setTaxonomyLibrary((prev) => {
      const next = [...prev, taxonomy];
      try { localStorage.setItem(`taxonomy_library_${id}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ─── Demo rail handlers ────────────────────────────────────────────────────
  const navigateToDemoStep = (step: number) => {
    setDemoStep(step);
    if (step === 1) {
      setActiveTab("taxonomy");
    } else if (step === 2) {
      setActiveTab("learners");
      setDemoRequestedSubTab("reconcile");
    } else if (step === 3) {
      setActiveTab("learners");
      setDemoRequestedSubTab("gap");
    } else if (step === 4) {
      setActiveTab("courses");
    }
  };

  const enterDemoMode = () => {
    setDemoMode(true);
    setDemoStep(1);
    setDemoIdentifiedGaps([]);
    navigateToDemoStep(1);
  };

  const exitDemoMode = () => {
    setDemoMode(false);
    setDemoStep(1);
    setDemoIdentifiedGaps([]);
  };

  const handleDemoGenerateCourse = (unitId: string, unitLabel: string) => {
    const topic = [
      `Generate a targeted training course to address this competency gap:`,
      ``,
      `Unit: ${unitId} — ${unitLabel}`,
      ``,
      `Design a focused course that builds this specific skill. Reference any attached workspace materials as source content where applicable.`,
    ].join("\n");
    setMaterialsChatTopic(topic);
    setMaterialsChatFiles(
      materials
        .filter((m) => m.file_type !== "url" && (m.text_content || m.summary))
        .map((m) => {
          const text = m.text_content || m.summary || "";
          const baseName = m.file_name.replace(/\.[^.]+$/, "");
          return new File([text], `${baseName}.txt`, { type: "text/plain" });
        })
    );
    setMaterialsChatOpen(true);
  };

  const fetchWorkspace = async () => {
    const { data, error } = await supabase
      .from("workspaces")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Error loading workspace",
        description: error.message,
      });
      navigate("/dashboard");
    } else {
      setWorkspace(data);
      setEditedWorkspace({ name: data.name, description: data.description || "" });
    }
    setLoading(false);
  };

  const fetchCourses = async () => {
    const { data, error } = await supabase
      .from("learning_projects")
      .select("*")
      .eq("workspace_id", id)
      .order("created_at", { ascending: false });

    if (!error) {
      setCourses(data || []);
    }
  };

  const fetchMaterials = async () => {
    const { data, error } = await supabase
      .from("materials")
      .select("*")
      .eq("workspace_id", id)
      .is("project_id", null)
      .order("created_at", { ascending: false });

    if (!error) {
      setMaterials(data || []);
    }
  };

  const handleUpdateWorkspace = async () => {
    const { error } = await supabase
      .from("workspaces")
      .update({
        name: editedWorkspace.name,
        description: editedWorkspace.description,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error updating workspace",
        description: error.message,
      });
    } else {
      toast({ title: "Workspace updated" });
      fetchWorkspace();
      setShowEditWorkspace(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    const { error } = await supabase
      .from("workspaces")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error deleting workspace",
        description: error.message,
      });
    } else {
      toast({ title: "Workspace deleted" });
      navigate("/dashboard");
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    const { error } = await supabase
      .from("materials")
      .delete()
      .eq("id", materialId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error deleting material",
        description: error.message,
      });
    } else {
      toast({ title: "Material deleted" });
      fetchMaterials();
    }
  };

  const handleViewMaterial = async (material: Material) => {
    if (material.file_type === "url") {
      const url = material.storage_path;
      if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast({ variant: "destructive", title: "Invalid URL", description: "This material has no valid URL." });
      }
    } else {
      try {
        const { data, error } = await supabase.storage
          .from("materials")
          .createSignedUrl(material.storage_path, 3600);
        if (error) throw error;
        if (data?.signedUrl) {
          window.open(data.signedUrl, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Cannot open file",
          description: err instanceof Error ? err.message : "Failed to load file",
        });
      }
    }
  };

  const handleCreateCourseFromMaterials = async (overrideIds?: Set<string>) => {
    const idsToUse = overrideIds ?? selectedMaterialIds;
    const selected = materials.filter((m) => idsToUse.has(m.id) && m.file_type !== "url");
    if (!selected.length) {
      toast({ variant: "destructive", title: "No materials selected", description: "Select at least one file material to create a course from." });
      return;
    }
    setLoadingMaterialFiles(true);
    try {
      const files: File[] = [];
      const skipped: string[] = [];

      for (const material of selected) {
        // Primary path: use extracted text already stored in the DB by process-material.
        // This avoids storage RLS issues entirely.
        const textSource = material.text_content || material.summary;
        if (textSource) {
          const baseName = material.file_name.replace(/\.[^.]+$/, "");
          files.push(new File([textSource], `${baseName}.txt`, { type: "text/plain" }));
          continue;
        }

        // Fallback: try downloading from storage (works once storage SELECT policy is fixed)
        try {
          const { data: signed } = await supabase.storage
            .from("materials")
            .createSignedUrl(material.storage_path, 120);
          if (signed?.signedUrl) {
            const res = await fetch(signed.signedUrl);
            if (res.ok) {
              const blob = await res.blob();
              files.push(new File([blob], material.file_name, { type: blob.type || "application/octet-stream" }));
              continue;
            }
          }
        } catch { /* storage unavailable */ }

        skipped.push(material.file_name);
      }

      if (!files.length) {
        toast({
          variant: "destructive",
          title: "Materials not ready",
          description: "No content could be read. The materials may still be processing — wait a moment and try again.",
        });
        return;
      }

      if (skipped.length > 0) {
        toast({ title: `${files.length} of ${selected.length} materials loaded`, description: `Still processing: ${skipped.join(", ")}` });
      }

      setMaterialsChatFiles(files);
      setMaterialsChatOpen(true);
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load materials." });
    } finally {
      setLoadingMaterialFiles(false);
    }
  };

  const handleGenerateCourseForLearner = (
    learner: LearnerProfile,
    gapUnitIds: string[],
    targetRole: string
  ) => {
    const gapLines = gapUnitIds.map((uid) => {
      const unit = unitById(uid);
      return `• ${uid}: ${unit?.label ?? uid}`;
    });

    const topic = [
      `Create a targeted training course for ${learner.name} (${learner.role}${learner.site ? `, ${learner.site}` : ""}).`,
      ``,
      `Target role: ${targetRole}`,
      `Competency gaps to address (${gapUnitIds.length} missing sub-competenc${gapUnitIds.length === 1 ? "y" : "ies"}):`,
      ...gapLines,
      ``,
      `Design the course to bridge these specific gaps and prepare ${learner.name.split(" ")[0]} for the ${targetRole} role. Focus on the missing competency units above. Relevant workspace materials are attached — use them as source content where applicable. You can also attach additional materials using the paperclip button.`,
    ].join("\n");

    // Collect all workspace materials with extracted text
    const files: File[] = [];
    for (const material of materials) {
      if (material.file_type === "url") continue;
      const textSource = material.text_content || material.summary;
      if (textSource) {
        const baseName = material.file_name.replace(/\.[^.]+$/, "");
        files.push(new File([textSource], `${baseName}.txt`, { type: "text/plain" }));
      }
    }

    setMaterialsChatTopic(topic);
    setMaterialsChatFiles(files);
    setMaterialsChatOpen(true);
  };

  if (loading || !workspace) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading workspace...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <span className="text-xl sm:text-2xl shrink-0">{workspace.icon || "📁"}</span>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">{workspace.name}</h1>
                {workspace.description && (
                  <p className="text-sm text-muted-foreground truncate hidden sm:block">{workspace.description}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            {!demoMode && (
              <Button variant="outline" size="sm" onClick={enterDemoMode} className="gap-1.5 hidden sm:inline-flex">
                <Sparkles className="h-3.5 w-3.5" />
                Start walkthrough
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowManageMembers(true)} data-tutorial="share-workspace">
              <Users className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button variant="outline" size="icon" onClick={() => setShowEditWorkspace(true)}>
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {demoMode && (
        <DemoRail
          step={demoStep}
          taxonomy={taxonomyLibrary[0]}
          learnerCount={demoLearnerCount}
          identifiedGaps={demoIdentifiedGaps}
          onNext={() => navigateToDemoStep(demoStep + 1)}
          onBack={() => navigateToDemoStep(demoStep - 1)}
          onExit={exitDemoMode}
        />
      )}

      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          setVisitedTabs((prev) => new Set(prev).add(v));
        }} className="space-y-6">
          <TabsList data-tutorial="workspace-tabs" className="flex w-full sm:w-auto overflow-x-auto flex-nowrap">
            <TabsTrigger value="courses" className="gap-2 shrink-0">
              <BookOpen className="h-4 w-4" />
              Courses ({courses.length})
            </TabsTrigger>
            <TabsTrigger value="materials" className="gap-2 shrink-0" data-tutorial-tab="materials">
              <FileText className="h-4 w-4" />
              Materials ({materials.length})
            </TabsTrigger>
            <TabsTrigger value="learners" className="gap-2 shrink-0">
              <ClipboardList className="h-4 w-4" />
              Learner Profiles
            </TabsTrigger>
            <TabsTrigger value="taxonomy" className="gap-2 shrink-0">
              <Wand2 className="h-4 w-4" />
              Build a taxonomy
            </TabsTrigger>
            <TabsTrigger value="assess" className="gap-2 shrink-0">
              <UserCheck className="h-4 w-4" />
              Assess individual
            </TabsTrigger>
          </TabsList>

          <TabsContent value="courses" forceMount={visitedTabs.has("courses") ? true : undefined} className="space-y-6">
            {/* Demo rail: gaps-to-courses section */}
            {demoMode && demoIdentifiedGaps.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 space-y-1">
                <p className="text-[13px] font-medium text-amber-900 mb-3">
                  {demoIdentifiedGaps.length} gap{demoIdentifiedGaps.length !== 1 ? "s" : ""} identified — generate a course for each
                </p>
                {demoIdentifiedGaps.map((g) => (
                  <div
                    key={g.unitId}
                    className="flex items-center justify-between gap-3 border-t border-amber-200 py-2.5"
                  >
                    <div className="text-[13px] min-w-0">
                      <span className="font-mono text-xs text-muted-foreground mr-2">{g.unitId}</span>
                      <span>{g.label}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        — {g.missingCount} employee{g.missingCount !== 1 ? "s" : ""} missing this
                      </span>
                    </div>
                    <Button
                      size="sm"
                      className="shrink-0 gap-1.5 text-xs h-7"
                      onClick={() => handleDemoGenerateCourse(g.unitId, g.label)}
                    >
                      <Sparkles className="h-3 w-3" />
                      Generate course
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold">Courses</h2>
                <p className="text-sm text-muted-foreground hidden sm:block">
                  Courses in this workspace can access shared materials
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/create?workspaceId=${encodeURIComponent(id!)}`)}
                className="inline-flex items-center justify-center gap-2 self-start sm:self-auto"
                data-tutorial="create-course"
              >
                <Plus className="mr-2 h-4 w-4" />
                New Course
              </Button>
            </div>

            {courses.length === 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>No courses yet</CardTitle>
                  <CardDescription>
                    Create your first course in this workspace
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate(`/create?workspaceId=${encodeURIComponent(id!)}`)}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                    data-tutorial="create-course"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Course
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {courses.map((course) => (
                  <ProjectCard
                    key={course.id}
                    project={course}
                    onUpdate={fetchCourses}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="materials" forceMount={visitedTabs.has("materials") ? true : undefined} className="space-y-6" data-tutorial-tab-content="materials">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold">Materials Library</h2>
                <p className="text-sm text-muted-foreground hidden sm:block">
                  Shared materials available to all courses in this workspace for RAG
                </p>
              </div>
              <Button onClick={() => setShowUploadMaterial(true)} data-tutorial="upload-material" size="sm" className="self-start sm:self-auto">
                <Upload className="mr-2 h-4 w-4" />
                Upload Materials
              </Button>
            </div>

            {materials.length === 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>No shared materials</CardTitle>
                  <CardDescription>
                    Upload materials to this workspace's library. They'll be available for RAG across all courses.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setShowUploadMaterial(true)}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Materials
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {materials.map((material) => {
                    const isSelected = selectedMaterialIds.has(material.id);
                    const isFile = material.file_type !== "url";
                    return (
                      <Card key={material.id} className={isSelected ? "ring-2 ring-primary" : ""}>
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {isFile && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setSelectedMaterialIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(material.id);
                                      else next.delete(material.id);
                                      return next;
                                    });
                                  }}
                                  className="h-4 w-4 shrink-0 cursor-pointer accent-primary mt-0.5"
                                  aria-label={`Select ${material.file_name}`}
                                />
                              )}
                              <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                              <CardTitle className="text-base truncate">
                                {material.file_name}
                              </CardTitle>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Material</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{material.file_name}"? This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteMaterial(material.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Badge variant="secondary" className="text-xs">
                            {material.file_type}
                          </Badge>
                          {material.file_type === "url" && material.storage_path && (
                            <a
                              href={material.storage_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1 mt-2 break-all"
                            >
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              {material.storage_path}
                            </a>
                          )}
                          {material.summary && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {material.summary}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-3 gap-2">
                            <p className="text-xs text-muted-foreground shrink-0">
                              {new Date(material.created_at!).toLocaleDateString()}
                            </p>
                            <div className="flex items-center gap-1.5">
                              {isFile && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1 text-xs"
                                  onClick={() => handleCreateCourseFromMaterials(new Set([material.id]))}
                                  disabled={loadingMaterialFiles}
                                >
                                  <Sparkles className="h-3 w-3" />
                                  Create course
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewMaterial(material)}
                                className="h-7"
                              >
                                {material.file_type === "url" ? (
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                ) : (
                                  <Eye className="h-3 w-3 mr-1" />
                                )}
                                {material.file_type === "url" ? "Open URL" : "View"}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Multi-select action bar */}
                {selectedMaterialIds.size > 0 && (
                  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 rounded-full border bg-card px-5 py-2.5 shadow-xl">
                    <span className="text-sm font-medium">
                      {selectedMaterialIds.size} material{selectedMaterialIds.size !== 1 ? "s" : ""} selected
                    </span>
                    <Button
                      size="sm"
                      className="rounded-full gap-2"
                      onClick={() => handleCreateCourseFromMaterials()}
                      disabled={loadingMaterialFiles}
                    >
                      {loadingMaterialFiles ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      Create course
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => setSelectedMaterialIds(new Set())}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="learners" forceMount={visitedTabs.has("learners") ? true : undefined}>
            {id && (
              <WorkspaceLearnerHub
                workspaceId={id}
                courses={courses.map((c) => ({
                  id: c.id,
                  title: c.title,
                  description: c.description,
                }))}
                taxonomyLibrary={taxonomyLibrary}
                importProfile={assessPendingProfile}
                requestedSubTab={demoRequestedSubTab}
                onSubTabConsumed={() => setDemoRequestedSubTab(null)}
                onLearnersChange={setDemoLearnerCount}
                onGapsComputed={setDemoIdentifiedGaps}
                onGenerateCourse={handleGenerateCourseForLearner}
              />
            )}
          </TabsContent>

          <TabsContent value="taxonomy" forceMount={visitedTabs.has("taxonomy") ? true : undefined}>
            <TaxonomyGeneratorTab
              onSave={handleTaxonomySaved}
              onGenerateCourse={(topic, files) => {
                setMaterialsChatTopic(topic);
                setMaterialsChatFiles(files);
                setMaterialsChatOpen(true);
              }}
              workspaceMaterials={materials
                .filter((m) => m.text_content || m.summary)
                .map((m) => ({
                  id: m.id,
                  fileName: m.file_name,
                  text: (m.text_content || m.summary)!,
                }))}
              demoPreload={demoMode ? CCM_TAXONOMY : undefined}
            />
          </TabsContent>

          <TabsContent value="assess" forceMount={visitedTabs.has("assess") ? true : undefined}>
            <IndividualAssessmentTab
              taxonomyLibrary={taxonomyLibrary}
              onExport={(profile) => {
                setAssessPendingProfile(profile);
                setActiveTab("learners");
              }}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Course creation from workspace materials or gap analysis */}
      <CourseCreationChat
        open={materialsChatOpen}
        onOpenChange={(open) => {
          setMaterialsChatOpen(open);
          if (!open) {
            setMaterialsChatFiles([]);
            setMaterialsChatTopic(undefined);
            setSelectedMaterialIds(new Set());
          }
        }}
        onSuccess={() => {
          fetchCourses();
          setMaterialsChatOpen(false);
          setMaterialsChatFiles([]);
          setMaterialsChatTopic(undefined);
          setSelectedMaterialIds(new Set());
          setActiveTab("courses");
        }}
        workspaceId={id}
        initialFiles={materialsChatFiles.length > 0 ? materialsChatFiles : undefined}
        initialTopic={materialsChatTopic}
      />

      {/* Upload Material Dialog - for workspace-level materials */}
      <UploadMaterialDialog
        open={showUploadMaterial}
        onOpenChange={setShowUploadMaterial}
        projectId=""
        workspaceId={id}
        onSuccess={fetchMaterials}
      />

      {/* Edit Workspace Dialog */}
      <Dialog open={showEditWorkspace} onOpenChange={setShowEditWorkspace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Workspace Settings</DialogTitle>
            <DialogDescription>
              Update workspace details or delete the workspace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Name</Label>
              <Input
                id="workspace-name"
                value={editedWorkspace.name}
                onChange={(e) => setEditedWorkspace({ ...editedWorkspace, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspace-description">Description</Label>
              <Textarea
                id="workspace-description"
                value={editedWorkspace.description}
                onChange={(e) => setEditedWorkspace({ ...editedWorkspace, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete Workspace</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete the workspace and all its materials. Courses will be kept but unassigned. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteWorkspace} className="bg-destructive text-destructive-foreground">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditWorkspace(false)}>Cancel</Button>
              <Button onClick={handleUpdateWorkspace}>Save Changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Members Dialog */}
      <ManageMembersDialog
        open={showManageMembers}
        onOpenChange={setShowManageMembers}
        type="workspace"
        resourceId={workspace.id}
        resourceName={workspace.name}
        isOwner={workspace.user_id === session?.user?.id}
      />
    </div>
  );
};

export default Workspace;

